import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChildren
} from '@angular/core';
import { MilitaryBranch } from '@cpd-app/core-module/models';

/**
 * Title keyword to glyph, in match order. "Space Force" and "Air Force" both
 * contain "force", so space is tested before air; anything unmatched takes the
 * default shield rather than a wrong branch's crest.
 */
const BRANCH_ICONS: [string, string][] = [
  ['army', 'army'],
  ['marine', 'marines'],
  ['navy', 'navy'],
  ['space', 'space'],
  ['air', 'air'],
  ['coast', 'coast']
];

/**
 * Branch of service, as one tap out of six.
 *
 * 01-account section 7: "Branch is a set of buttons, not a select". Section 8
 * adds the part that makes that safe: the six are a `radiogroup`, arrow-key
 * navigable, not six tab stops. A member tabs into the group once, arrows to
 * their branch, and tabs out.
 *
 * This is deliberately not `cpd-military-skills-branch`, which is the same
 * choice drawn as a plain grid of buttons with no group semantics and no
 * keyboard model. Bringing that one up to this is worth doing and is not this
 * change.
 */
@Component({
  selector: 'cpd-branch-picker',
  templateUrl: './branch-picker.component.html',
  styleUrls: ['./branch-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BranchPickerComponent {
  @Input() public branches: MilitaryBranch[] = [];
  @Input() public selectedBranchCode: string;

  /** Ids of the elements labelling and describing the group, for aria wiring. */
  @Input() public labelledBy: string;
  @Input() public describedBy: string;

  @Output() public branchSelected = new EventEmitter<MilitaryBranch>();

  @ViewChildren('branchOption')
  private options: QueryList<ElementRef<HTMLButtonElement>>;

  /**
   * Roving tabindex. Exactly one option is tabbable: the selected one, or the
   * first when nothing is selected yet, so tabbing into an untouched group
   * lands somewhere sensible rather than nowhere.
   */
  public isTabbable(branch: MilitaryBranch, index: number): boolean {
    if (this.selectedBranchCode) {
      return branch.branchCode === this.selectedBranchCode;
    }

    return index === 0;
  }

  public select(branch: MilitaryBranch): void {
    this.branchSelected.emit(branch);
  }

  /**
   * Arrow keys move and select in one step, which is what a radiogroup does:
   * the selection follows focus. Home and End jump to the ends. The grid wraps
   * because six options in a three-wide grid have no natural edge to stop at.
   */
  public onKeydown(event: KeyboardEvent, index: number): void {
    const count = this.branches.length;

    if (!count) {
      return;
    }

    let next: number;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (index + 1) % count;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (index - 1 + count) % count;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = count - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.select(this.branches[next]);
    this.options?.get(next)?.nativeElement.focus();
  }

  /**
   * Which glyph a branch gets. Matched on the title rather than on branchCode:
   * the codes are single letters owned by the database and the set has grown
   * before, so a title match degrades to the default shield instead of
   * silently mislabelling a branch.
   */
  public iconFor(branch: MilitaryBranch): string {
    const title = (branch?.branchTitle || '').toLowerCase();
    const match = BRANCH_ICONS.find(([keyword]) => title.includes(keyword));

    return match ? match[1] : 'default';
  }
}
